import { City, Country, State } from "country-state-city";
import { get, isEmpty } from "lodash";
import {
  convertLocalNumber,
  getPhoneCountryCode,
} from "../components/CustomPhoneInput";
import { buId, custom_permissions, tenantId } from "../config/localStorage";
import { adminType, CLIENT_PERMISSIONS } from "../constant/constant";
import { resetLoaderState } from "../store/Slices/loading";
import {
  getConfiguration,
  // getCustomEntityData,
  getUserSettings
} from "../utils/ApiHandler";
import {
  BAR_MENU,
  defaultTheme,
  MIN_LENGTH,
  URLSAFECHARS,
  USER_KEYS
} from "../utils/const";
import { permissionsFn, redirectToPath } from "./com-helper";
import { getLocalStorageItem } from "./localStorageHelper";
import TagManager from 'react-gtm-module';

// Helper function to check permissions and roles
const hasPermissionsAndRoles = (
  nodePermissions = [],
  nodeRoles = [],
  permissions = [],
  roles = []
) => {
  const hasPermission = nodePermissions.some((permission) =>
    permissions.includes(permission)
  );
  const hasRole = nodeRoles
    ? nodeRoles.length === 0 || nodeRoles.some((role) => roles.includes(role))
    : true;
  return hasPermission && hasRole;
};

// Recursive function to traverse child nodes
const traverseChildren = (children, permissions, roles, matchedNodes) => {
  for (const key in children) {
    const child = children[key];

    if (
      child?.permissions &&
      child?.route &&
      hasPermissionsAndRoles(
        child.routePermissions || child.permissions,
        child.roles || [],
        permissions,
        roles
      )
    ) {
      matchedNodes.push(child); // Add matched child node
    }

    if (child.child) {
      traverseChildren(child.child, permissions, roles, matchedNodes);
    }
  }
};

// Function to check extra routes
const checkExtraRoutes = (extraRoutes, permissions, roles, matchedNodes) => {
  extraRoutes?.forEach((item) => {
    if (
      hasPermissionsAndRoles(
        item.permissions,
        item.roles || [],
        permissions,
        roles
      ) &&
      item?.route
    ) {
      matchedNodes.push(item);
    }
  });
};

export const getPath = (permissions, roles, inputArray = Object.values(BAR_MENU)) => {
  const matchedNodes = [];
  inputArray.forEach((idObject) => {
    if (idObject.child) {
      traverseChildren(idObject.child, permissions, roles, matchedNodes);
    }
    checkExtraRoutes(idObject.extraRoutes, permissions, roles, matchedNodes);
  });
  return matchedNodes.length > 0 ? matchedNodes[0] : null;
};

export const getUserMetaData = (_user) => {
  const metaData = {},
    user = {};
  for (let key in _user) {
    if (USER_KEYS.includes(key)) user[key] = _user[key];
    else metaData[key] = _user[key];
  }
  return { metaData, user };
};

export const setUserCookies = (_user) => {
  const { metaData, user } = getUserMetaData(_user);
  // Cookies.set("user", JSON.stringify(user), {
  //   expires: 1,
  //   secure: true,
  //   sameSite: "Strict",
  // });

  // Set profile display initials
  const firstName = user?.first_name;
  const lastName = user?.last_name;
  const fullName = `${firstName} ${lastName}`.trim();

  if (firstName && lastName) {
    localStorage.setItem("profileDP", fullName);
  }
  localStorage.setItem("user", JSON.stringify(metaData));
};

// Prefer the user-selected BU, else the tenant default (non-admins only).
const resolveLoginBu = (selected_business_unit, users, isAdminLogin) => {
  if (selected_business_unit?.business_unit_id) return selected_business_unit;
  if (isAdminLogin) return null;
  return findDefaultBu(users?.business_units || []);
};

export const processLogin = async (res, dispatch, selected_business_unit) => {
  if (res?.status === 200) {
    const users = res.data.data;
    const user = setUserLocalStorage(users);
    const path = !isEmpty(res) && getPath(permissionsFn(user), user.roles);
    const isAdminLogin = (users?.roles || []).some((role) => role in adminType);
    const resolvedBu = resolveLoginBu(selected_business_unit, users, isAdminLogin);
    const setting = {
      platform: {
        theme: defaultTheme,
      },
    }

    if (user.tenant_id && resolvedBu?.business_unit_id) {
      const settingRes = await getUserSettings(
        user.access,
        user.tenant_id,
        resolvedBu.business_unit_id
      );
      localStorage.setItem("settings", JSON.stringify(settingRes?.data?.responseData?.response || setting));

      const configurationResult = await getConfiguration(
        user.access,
        user.tenant_id,
        resolvedBu.business_unit_id
      );

      // await getCustomEntityData(user.tenant_id,
      //   selected_business_unit?.business_unit_id)
      if (configurationResult?.status === 200) {
        setUserCookies(user);
        redirectToPath(path);
      }
    } else {
      const settingRes = await getUserSettings(
        user.access,
        user.tenant_id,
        ""
      );
      localStorage.setItem("settings", JSON.stringify(settingRes?.data?.responseData?.response || setting));
      setUserCookies(user);
      redirectToPath(path);
    }
    localStorage.removeItem("sso_credentials");
    TagManager.dataLayer({
      dataLayer: {
        event: 'login',
        scai_user: user.user_id,
        scai_tenant: user.tenant_id,
      }
    });
  }
  dispatch(resetLoaderState());
};

export const generateUrlSafeString = () => {
  let result = "";
  const charsLength = URLSAFECHARS.length;
  for (let i = 0; i < MIN_LENGTH; i++) {
    const array = new Uint32Array(1);
    result += URLSAFECHARS.charAt(
      Math.floor(crypto.getRandomValues(array) % charsLength)
    );
  }
  return result;
};

export function arrayBufferToBase64Url(buffer) {
  const byteArray = new Uint8Array(buffer);
  const base64 = btoa(String.fromCodePoint.apply(null, byteArray));
  return base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/={0,100}$/, "");
}

// Function to compute SHA-256 hash and encode it as Base64URL
export async function sha256Base64UrlEncoded(data) {
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);

  const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
  const base64UrlEncoded = arrayBufferToBase64Url(hashBuffer);

  return base64UrlEncoded;
}

export const isNotificationEnabled = () => {
  return buId && tenantId ? true : false;
};

export const createBuOptions = (business_units) => {
  return business_units.map((bu) => {
    return {
      value: bu.id,
      label: bu.name,
      is_default: bu.is_default,
      teamId: bu?.team?.id,
    };
  });
};

export const isAdminUser = () => {
  const user = getLocalStorageItem("user");
  const roles = get(user, "roles", []);
  for (const role of roles) {
    if (role in adminType) return true;
  }
  return false;
};

export const superAdminUser = () => {
  const user = getLocalStorageItem("user");
  const roles = get(user, "roles", []);
  for (let i = 0; i < roles.length; i++) {
    let role = roles[i];
    if (role === adminType["3SCAdmin"]) return true;
  }
  return false;
};

//Country state ciy dropdown Options
export const updatedStates = (countryId) =>
  State.getStatesOfCountry(countryId).map((state) => ({
    label: state.name,
    value: state.isoCode,
    ...state,
  }));

export const updatedCities = (countryId, stateId) =>
  City.getCitiesOfState(countryId, stateId).map((city) => ({
    label: city.name,
    value: city.isoCode,
    ...city,
  }));

const formatCustomEntityPermissions = (arr) =>
  arr
    .filter(p => p.endsWith('_VIEW'))
    .map(permission => {
      const base = permission.slice(0, -5); // remove '_VIEW'

      const name = base
        .toLowerCase()
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

      return {
        name,
        permissions: [permission],
        route: `/edm/${base?.toLowerCase().replace(/_/g, '-')}`
      };
    });

export const customEntityRoutes = () => {
  const customEntityData = formatCustomEntityPermissions(custom_permissions?.edm || []);
  return customEntityData;
}

export const updatedCountries = Country.getAllCountries().map((country) => ({
  label: country.name,
  value: country.isoCode,
  ...country,
}));

export const findCountry = (countryName) => {
  return Country.getAllCountries().find(
    (country) => country.name === countryName
  );
};

export const findState = (countryId, stateName) => {
  const states = State.getStatesOfCountry(countryId);
  return states.find((state) => state.name === stateName);
};

export const createPayloadUserEdit = (formData = {}, userId) => {
  const data = formData?.data ?? {};

  const localNumber = convertLocalNumber(formData?.mobile ?? "");
  const countryCode = getPhoneCountryCode(formData?.mobile ?? "");

  const storedBU = getLocalStorageItem("selected_business_unit") ?? {};
  const selectedBuId = storedBU?.business_unit_id ?? null;

  const businessUnits = (data?.business_units ?? []).map((item) => ({
    business_unit: item?.id ?? "",
    is_default: item?.id === selectedBuId,
    team: item?.team?.id ?? "",
    roles: (item?.roles ?? []).map((rl) => rl?.role?.id ?? ""),
  }));

  const payload = {
    id: userId ?? "",

    organization: data?.organization?.id ?? "",
    business_unit: data?.business_unit?.id ?? "",
    team: data?.team?.id ?? "",

    first_name: formData?.first_name ?? "",
    last_name: formData?.last_name ?? "",
    email: formData?.email ?? "",

    mobile: localNumber ?? "",
    mobile_country_code: countryCode ?? "",

    address_line1: formData?.address_line1 ?? "",
    address_line2: formData?.address_line2 ?? "",
    country: formData?.country?.label ?? "",
    state:
      formData?.state?.value !== "" ? formData?.state?.label ?? "" : "",
    city:
      formData?.city?.value !== "" ? formData?.city?.label ?? "" : "",
    pincode: formData?.pincode ?? "",

    business_units: businessUnits ?? [],
  };

  return payload;
};

export const createUserData = ({ data }) => {
  const country = findCountry(data?.country);
  const state = findState(country?.isoCode, data?.state);
  let roles = [];
  let buName = "";
  let teamName = "";
  const bu = getLocalStorageItem("selected_business_unit");
  const buId = bu?.business_unit_id;
  const businessUnits = data?.business_units;
  const businessUnitsLn = businessUnits?.length;
  for (let i = 0; i < businessUnitsLn; i++) {
    const buItem = businessUnits[i];
    if (buItem?.id !== buId) continue;
    buName = buItem?.name;
    teamName = buItem?.team?.name;
    const rl = buItem?.roles;
    const rlLn = rl?.length;
    for (let i = 0; i < rlLn; i++) {
      const rlItem = rl[i];
      const name = rlItem?.role?.name;
      roles.push(name);
    }
  }
  let obj = {
    organization: data?.organization?.name || "",
    first_name: data?.first_name || "",
    last_name: data?.last_name || "",
    email: data?.email || "",
    mobile_country_code: data?.mobile_country_code || "",
    mobile: data?.mobile_country_code + data?.mobile || "",
    team: teamName || "",
    business_unit: buName || "",
    address_line1: data?.address_line1 || "",
    address_line2: data?.address_line2 || "",
    country: country?.name || data?.country ? {
      label: country?.name || data?.country || "",
      value: country?.isoCode || data?.country || "",
    } : null,
    state: state?.name || data?.state ? {
      label: state?.name || data?.state || "",
      value: state?.isoCode || data?.state || "",
    } : null,
    city: data?.city ? {
      label: data?.city || "",
      value: data?.city || "",
    } : null,
    pincode: data?.pincode || "",
    roles: roles?.join(", ") || "",
    data,
  };
  return obj;
};

export const userObj = (user, id) => {
  let permissions = [];
  let roles = [];
  let businessUnit = user?.business_units;
  let businessUnitLn = businessUnit?.length;
  if (!businessUnitLn) return user;
  for (let i = 0; i < businessUnitLn; i++) {
    let buItem = businessUnit[i];
    let buId = buItem?.business_unit_id ?? buItem?.id;
    if (id && buId !== id) continue;
    if (!id && !buItem?.is_default) continue;
    roles = buItem?.roles;
    permissions = buItem?.permissions;
  }
  let newObj = {
    ...user,
    permissions: permissions || user?.permissions || [],
    roles: roles || user?.roles || [],
  };
  return newObj;
};

export const setUserLocalStorage = (user, id) => {
  let isAdmin = false;
  let roles = user?.roles;
  let newUser = userObj(user, id);

  const rolesLn = roles?.length;
  for (let i = 0; i < rolesLn; i++) {
    let role = roles[i];
    if (role in adminType) {
      newUser.roles = [...(newUser?.roles || []), role];
      newUser.permissions = [...(newUser?.permissions || []), ...CLIENT_PERMISSIONS];
      isAdmin = true;
      break;
    }
  }

  if (id) {
    return newUser;
  } else {
    let userObj = isAdmin && user?.permissions ? user : newUser;
    return userObj;
  }
};

export const findDefaultBu = (buArr) => {
  const bu = buArr.find((bu) => bu.is_default) || buArr?.[0];
  return {
    business_unit_id: bu?.business_unit_id ?? bu?.id,
    business_unit_name: bu?.business_unit_name ?? bu?.name,
  };
};

export function truncateString(input, num) {
  if (input.length > num) {
    return input.substring(0, num) + "...";
  }
  return input;
}

export const currentYear = () => {
  return new Date().getFullYear();
};

export const hasAiAssistantAccess = () => {
  const user = getLocalStorageItem("user");
  const permissions = get(user, "permissions", []);
  const roles = get(user, "roles", []);

  const hasAiAssistantPermission = permissions.includes("AI_ASSISTANT");

  const hasAdminRole = roles.includes(adminType["3SCAdmin"]) || roles.includes(adminType["Client Admin"]);

  return hasAiAssistantPermission || hasAdminRole;
};

